// canvas/canvasNodes.js
// Node type definitions: ports, default params, solve(), P&ID symbol geometry.
/* eslint-disable no-unused-vars */

import {
  makeStream, emptyStream, cloneStream, separatorFlash, mixStreams,
  firedHeater, heatExchanger, centrifugalCompressor,
  kremserAbsorber, stripper, trayHoldupColumn, chiller, reboiler, splitter,
  jtValve, fugDistillation, rigorousDistillation, partialCondenser,
  COMP_LIST, N_COMP, mixtureMW, R,
} from './canvasThermo';

export const NODE_TYPES = {

  FEED: {
    label: 'Feed', category: 'boundary', color: 'teal',
    w: 100, h: 50,
    ports: [{ key: 'out', label: 'Out', kind: 'out', phase: 'any', side: 'right', pct: 0.5 }],
    defaultParams: {
      name: '',
      T_C: 25, P_bar: 60, F_kmolh: 1000,
      Methane: 82, Ethane: 8, Propane: 4,
      'n-Butane': 2, 'n-Heptane': 0.2, 'n-Octane': 0.1, 'n-Nonane': 0.05,
      Nitrogen: 1.5, 'Carbon Dioxide': 1.5, 'Hydrogen Sulfide': 0.3, Water: 0.35,
    },
    solve(params, _inlets) {
      const z = new Float64Array(N_COMP);
      let sum = 0;
      COMP_LIST.forEach((c, i) => { z[i] = params[c.name] || 0; sum += z[i]; });
      if (sum > 0) for (let i = 0; i < N_COMP; i++) z[i] /= sum;
      return {
        streams: { out: makeStream('out', params.T_C + 273.15, params.P_bar, params.F_kmolh, Array.from(z)) },
        results: { 'Flow (kmol/h)': params.F_kmolh.toFixed(1), 'T (°C)': params.T_C, 'P (bar)': params.P_bar },
      };
    },
  },

  SINK: {
    label: 'Sink', category: 'boundary', color: 'gray',
    w: 100, h: 50,
    ports: [{ key: 'in', label: 'In', kind: 'in', phase: 'any', side: 'left', pct: 0.5 }],
    defaultParams: { name: 'Product' },
    solve(params, inlets) {
      const s = inlets.in;
      if (!s?.valid) return { streams: {}, results: { Status: 'No feed' } };
      const MW = mixtureMW(s.z);
      return {
        streams: {},
        results: { 'Flow (kmol/h)': s.F_kmols.toFixed(1), 'Mass (kg/h)': (s.F_kmols * MW).toFixed(1), 'T (°C)': (s.T_K - 273.15).toFixed(1), 'P (bar)': s.P_bar.toFixed(2), Phase: s.phase },
      };
    },
  },

  SEPARATOR: {
    label: 'Separator', category: 'separation', color: 'blue',
    w: 70, h: 140,
    ports: [
      { key: 'in',  label: 'Feed', kind: 'in',  phase: 'any', side: 'left',   pct: 0.5 },
      { key: 'vap', label: 'Vap',  kind: 'out', phase: 'V',   side: 'top',    pct: 0.5 },
      { key: 'liq', label: 'Liq',  kind: 'out', phase: 'L',   side: 'bottom', pct: 0.5 },
    ],
    defaultParams: { name: 'Sep' },
    solve(params, inlets) {
      const s = inlets.in;
      if (!s?.valid || s.F_kmols === 0) return { streams: { vap: emptyStream('vap'), liq: emptyStream('liq') }, results: { Status: 'No feed' } };
      const { vapour, liquid, beta } = separatorFlash(s, 'vap', 'liq');
      return {
        streams: { vap: vapour, liq: liquid },
        results: { 'Vap%': (beta * 100).toFixed(1) + '%', 'Vap (kmol/h)': vapour.F_kmols.toFixed(1), 'Liq (kmol/h)': liquid.F_kmols.toFixed(1) },
      };
    },
  },

  MIXER: {
    label: 'Mixer', category: 'utility', color: 'purple',
    w: 60, h: 60,
    ports: [
      { key: 'in1', label: '', kind: 'in',  phase: 'any', side: 'left',  pct: 0.28 },
      { key: 'in2', label: '', kind: 'in',  phase: 'any', side: 'left',  pct: 0.72 },
      { key: 'out', label: '', kind: 'out', phase: 'any', side: 'right', pct: 0.5  },
    ],
    defaultParams: { name: '' },
    solve(params, inlets) {
      const ins = Object.values(inlets).filter(s => s?.valid && s.F_kmols > 0);
      if (ins.length === 0) return { streams: { out: emptyStream('out') }, results: { Status: 'No feed' } };
      const out = mixStreams(ins, 'out');
      return { streams: { out }, results: { 'Flow (kmol/h)': out.F_kmols.toFixed(1), 'T (°C)': (out.T_K - 273.15).toFixed(1), Phase: out.phase } };
    },
  },

  SPLITTER: {
    label: 'Splitter', category: 'utility', color: 'gray',
    w: 60, h: 60,
    ports: [
      { key: 'in',   label: '', kind: 'in',  phase: 'any', side: 'left',  pct: 0.5  },
      { key: 'out1', label: '', kind: 'out', phase: 'any', side: 'right', pct: 0.28 },
      { key: 'out2', label: '', kind: 'out', phase: 'any', side: 'right', pct: 0.72 },
    ],
    defaultParams: { name: '', split1: 0.5 },
    solve(params, inlets) {
      const s = inlets.in;
      if (!s?.valid) return { streams: { out1: emptyStream('out1'), out2: emptyStream('out2') }, results: { Status: 'No feed' } };
      const [out1, out2] = splitter(s, [params.split1, 1 - params.split1], ['out1', 'out2']);
      return { streams: { out1, out2 }, results: { 'Split%': (params.split1 * 100).toFixed(0) + '%', 'Out1': out1.F_kmols.toFixed(1), 'Out2': out2.F_kmols.toFixed(1) } };
    },
  },

  AIRCOOLER: {
    label: 'Air Cooler', category: 'heat', color: 'blue',
    w: 110, h: 70,
    ports: [
      { key: 'in',  label: '', kind: 'in',  phase: 'any', side: 'left',  pct: 0.5 },
      { key: 'out', label: '', kind: 'out', phase: 'any', side: 'right', pct: 0.5 },
    ],
    defaultParams: { name: '', outT_C: 25 },
    solve(params, inlets) {
      const s = inlets.in;
      if (!s?.valid) return { streams: { out: emptyStream('out') }, results: { Status: 'No feed' } };
      const T2 = params.outT_C + 273.15;
      const { stream, duty_kW } = firedHeater(s, T2, 'out');
      const duty_abs = Math.abs(duty_kW);
      // Liquid stays liquid after cooling; mixed/vapour may condense
      stream.phase = T2 < s.T_K ? (s.phase === 'L' ? 'L' : 'VL') : s.phase;
      return {
        streams: { out: stream },
        results: {
          'Outlet T (°C)': params.outT_C,
          'Duty (kW)': duty_abs.toFixed(0),
          'In T (°C)': (s.T_K - 273.15).toFixed(1),
        }
      };
    },
  },

  HEATER: {
    label: 'Fired Heater', category: 'heat', color: 'amber',
    w: 110, h: 80,
    ports: [
      { key: 'in',  label: '', kind: 'in',  phase: 'any', side: 'left',  pct: 0.5 },
      { key: 'out', label: '', kind: 'out', phase: 'any', side: 'right', pct: 0.5 },
    ],
    defaultParams: { name: '', outT_C: 150 },
    solve(params, inlets) {
      const s = inlets.in;
      if (!s?.valid) return { streams: { out: emptyStream('out') }, results: { Status: 'No feed' } };
      const { stream, duty_kW } = firedHeater(s, params.outT_C + 273.15, 'out');
      return { streams: { out: stream }, results: { 'Outlet T (°C)': params.outT_C, 'Duty (kW)': duty_kW.toFixed(0) } };
    },
  },

  HX: {
    label: 'Heat Exchanger', category: 'heat', color: 'coral',
    w: 90, h: 90,
    ports: [
      { key: 'hotIn',   label: 'Hot in',   kind: 'in',  phase: 'any', side: 'left',  pct: 0.28 },
      { key: 'coldIn',  label: 'Cold in',  kind: 'in',  phase: 'any', side: 'left',  pct: 0.72 },
      { key: 'hotOut',  label: 'Hot out',  kind: 'out', phase: 'any', side: 'right', pct: 0.28 },
      { key: 'coldOut', label: 'Cold out', kind: 'out', phase: 'any', side: 'right', pct: 0.72 },
    ],
    defaultParams: { name: '', UA_kWK: 50 },
    solve(params, inlets) {
      const hot = inlets.hotIn, cold = inlets.coldIn;
      if (!hot?.valid || !cold?.valid) return { streams: { hotOut: emptyStream('hotOut'), coldOut: emptyStream('coldOut') }, results: { Status: 'Missing inlet' } };
      const { hotOut, coldOut, duty_kW, effectiveness, LMTD_K } = heatExchanger(hot, cold, params.UA_kWK, 'hotOut', 'coldOut');
      const crossWarning = hotOut.T_K < cold.T_K + 0.5 ? '⚠ near cross' : '';
      return {
        streams: { hotOut, coldOut },
        results: {
          'Duty (kW)': duty_kW.toFixed(0),
          'LMTD (°C)': LMTD_K ? LMTD_K.toFixed(1) : '—',
          'Eff': (effectiveness * 100).toFixed(1) + '%',
          'Hot in→out (°C)': `${(hot.T_K-273.15).toFixed(1)}→${(hotOut.T_K-273.15).toFixed(1)}`,
          'Cold in→out (°C)': `${(cold.T_K-273.15).toFixed(1)}→${(coldOut.T_K-273.15).toFixed(1)}`,
          'Hot F (kmol/h)': hot.F_kmols.toFixed(1),
          'Cold F (kmol/h)': cold.F_kmols.toFixed(1),
          ...(crossWarning ? {'⚠ Warning': crossWarning} : {}),
        }
      };
    },
  },

  CHILLER: {
    label: 'Chiller', category: 'heat', color: 'blue',
    w: 90, h: 90,
    ports: [
      { key: 'in',  label: '', kind: 'in',  phase: 'any', side: 'left',  pct: 0.5 },
      { key: 'out', label: '', kind: 'out', phase: 'any', side: 'right', pct: 0.5 },
    ],
    defaultParams: { name: '', outT_C: -20 },
    solve(params, inlets) {
      const s = inlets.in;
      if (!s?.valid) return { streams: { out: emptyStream('out') }, results: { Status: 'No feed' } };
      const { stream, duty_kW, COP, compPower_kW, vapourFraction } = chiller(s, params.outT_C + 273.15, 'out');
      return { streams: { out: stream }, results: { 'Outlet T (°C)': params.outT_C, 'Duty (kW)': duty_kW.toFixed(0), COP: COP.toFixed(2), 'Refrig (kW)': compPower_kW.toFixed(0), 'Vap%': (vapourFraction * 100).toFixed(1) + '%' } };
    },
  },

  REBOILER: {
    label: 'Reboiler', category: 'heat', color: 'amber',
    w: 120, h: 60,
    ports: [
      { key: 'in',     label: '', kind: 'in',  phase: 'L', side: 'left',   pct: 0.5 },
      { key: 'vapOut', label: '', kind: 'out', phase: 'V', side: 'top',    pct: 0.7 },
      { key: 'liqOut', label: '', kind: 'out', phase: 'L', side: 'right',  pct: 0.5 },
    ],
    defaultParams: { name: '', vapFraction: 0.3 },
    solve(params, inlets) {
      const s = inlets.in;
      if (!s?.valid) return { streams: { vapOut: emptyStream('vapOut'), liqOut: emptyStream('liqOut') }, results: { Status: 'No feed' } };
      const { vapOut, liqOut, duty_kW, T_reb_C } = reboiler(s, params.vapFraction, 'vapOut', 'liqOut');
      return { streams: { vapOut, liqOut }, results: { 'Duty (kW)': duty_kW.toFixed(0), 'T reb (°C)': T_reb_C.toFixed(1), 'Vap (kmol/h)': vapOut.F_kmols.toFixed(1) } };
    },
  },

  COMPRESSOR: {
    label: 'Compressor', category: 'rotating', color: 'green',
    w: 80, h: 80,
    ports: [
      { key: 'in',  label: '', kind: 'in',  phase: 'V', side: 'left',  pct: 0.5 },
      { key: 'out', label: '', kind: 'out', phase: 'V', side: 'right', pct: 0.5 },
    ],
    defaultParams: { name: '', P2_bar: 120, eta_poly: 0.78 },
    solve(params, inlets) {
      const s = inlets.in;
      if (!s?.valid) return { streams: { out: emptyStream('out') }, results: { Status: 'No feed' } };
      const { stream, power_kW, T2_C } = centrifugalCompressor(s, params.P2_bar, params.eta_poly, 'out');
      return { streams: { out: stream }, results: { 'Power (kW)': power_kW.toFixed(0), 'Out T (°C)': T2_C.toFixed(1), 'Out P (bar)': params.P2_bar, Ratio: (params.P2_bar / s.P_bar).toFixed(2) } };
    },
  },

  PUMP: {
    label: 'Pump', category: 'rotating', color: 'green',
    w: 80, h: 60,
    ports: [
      { key: 'in',  label: '', kind: 'in',  phase: 'L', side: 'left',  pct: 0.5 },
      { key: 'out', label: '', kind: 'out', phase: 'L', side: 'right', pct: 0.5 },
    ],
    defaultParams: {
      name: '', P2_bar: 10, eta: 0.75,
      setFlow_kmolh: 0,      // fixed circulation rate (surge tank model)
      surgeVol_m3: 50,
      leanOilC7: 6,
      leanOilC8: 50,
      leanOilC9: 44,
    },
    // Internal state: persist lean oil composition between solve() calls
    // Stored on the node params as _z_inventory (array of N_COMP mole fractions)
    solve(params, inlets) {
      const s = inlets.in;
      const F_target = params.setFlow_kmolh > 0 ? params.setFlow_kmolh : (s?.F_kmols || 0);
      if (F_target < 1e-9) return { streams: { out: emptyStream('out') }, results: { Status: 'No flow set' } };

      // ── Surge drum composition model ────────────────────────────────────
      // The surge drum holds a liquid inventory. At each solve() call:
      //   1. What arrives from ROLO (s.z, s.F_kmols) mixes into the drum
      //   2. The drum outputs at setFlow_kmolh with its current composition
      //   3. The drum composition shifts toward what arrived (damped by volume)
      //
      // Moles in drum = surgeVol_m3 × liquid_density / MW × 1000
      // For C5/C6 mixture: density ≈ 630 kg/m³, MW ≈ 82 → ~7700 mol/m³ = 7.7 kmol/m³
      const LEAN_OIL_DENSITY = 7.7; // kmol/m³ (C5/C6 liquid at ~25°C)
      const surgeHoldup_kmol = (params.surgeVol_m3 || 50) * LEAN_OIL_DENSITY;

      // Get or initialise drum composition
      // _z_inventory is stored on params between iterations (mutable state)
      if (!params._z_inventory || params._z_inventory.length !== N_COMP) {
        params._z_inventory = new Array(N_COMP).fill(0);
        const c7 = Math.max(0, params.leanOilC7 || 6) / 100;
        const c8 = Math.max(0, params.leanOilC8 || 50) / 100;
        const c9 = Math.max(0, params.leanOilC9 || 44) / 100;
        const total = c7 + c8 + c9;
        params._z_inventory[4] = total > 0 ? c7 / total : 0.06; // C7
        params._z_inventory[5] = total > 0 ? c8 / total : 0.50; // C8
        params._z_inventory[6] = total > 0 ? c9 / total : 0.44; // C9
      }

      const z_drum = params._z_inventory; // current drum composition (mole fractions)

      // If we have a valid inlet (ROLO returning lean oil), blend it into the drum
      if (s?.valid && s.F_kmols > 0.01) {
        // Residence time effect: fraction of drum displaced per timestep
        // dz/dt = (F_in/V_drum) * (z_in - z_drum)
        // Each solve() call is one "timestep". alpha = F_in / (F_in + V_drum/τ)
        // Simple: alpha = F_in_kmolh / (F_in_kmolh + surgeHoldup_kmol)
        const alpha = s.F_kmols / (s.F_kmols + surgeHoldup_kmol);

        // Only blend if the inlet looks like reasonable lean oil (not mostly water)
        const waterFrac = s.z[10] || 0;
        const leanOilFrac = (s.z[4] || 0) + (s.z[5] || 0) + (s.z[6] || 0); // C7+C8+C9
        const looksReasonable = waterFrac < 0.5 && leanOilFrac > 0.1;

        if (looksReasonable) {
          for (let i = 0; i < N_COMP; i++) {
            z_drum[i] = (1 - alpha) * z_drum[i] + alpha * s.z[i];
          }
          // Renormalise
          let sum = 0; for (let i = 0; i < N_COMP; i++) sum += z_drum[i];
          if (sum > 0) for (let i = 0; i < N_COMP; i++) z_drum[i] /= sum;
        }
        // If inlet looks corrupted (water-dominated), ignore it — drum holds its composition
      }

      // Build output stream from drum composition
      const z_out = new Float64Array(N_COMP);
      for (let i = 0; i < N_COMP; i++) z_out[i] = Math.max(0, z_drum[i]);

      const T_out = s?.valid ? s.T_K : 298.15;
      const MW = mixtureMW(z_out);
      const dP_Pa = (params.P2_bar - (s?.P_bar || params.P2_bar - 3)) * 1e5;
      const power_kW = dP_Pa > 0
        ? (F_target * MW / 3600) * (dP_Pa / (750 * 1000)) / (params.eta || 0.75)
        : 0;

      const deficit = s?.valid ? Math.max(0, F_target - s.F_kmols) : F_target;
      const c7c8c9 = ((z_drum[4] + z_drum[5] + z_drum[6]) * 100).toFixed(1);
      const water = (z_drum[10] * 100).toFixed(2);

      const out = {
        id: 'out', T_K: T_out, P_bar: params.P2_bar,
        F_kmols: F_target, z: z_out, phase: 'L', valid: true,
      };

      return {
        streams: { out },
        results: {
          'Power (kW)': Math.max(0, power_kW).toFixed(1),
          'Flow (kmol/h)': F_target.toFixed(1),
          'Out P (bar)': params.P2_bar,
          'Drum C7-C9 (%)': c7c8c9,
          'Drum H₂O (%)': water,
          'Surge (kmol)': surgeHoldup_kmol.toFixed(0),
          ...(deficit > 0.01 * F_target ? { 'Tank': `⚠ deficit ${deficit.toFixed(1)} kmol/h` } : { 'Tank': '✓ OK' }),
        }
      };
    },
  },

  ABSORBER: {
    label: 'Absorber', category: 'separation', color: 'teal',
    w: 70, h: 160,
    ports: [
      { key: 'gasIn',      label: 'Gas in',     kind: 'in',  phase: 'V', side: 'bottom', pct: 0.3 },
      { key: 'solventIn',  label: 'Lean oil',   kind: 'in',  phase: 'L', side: 'top',    pct: 0.7 },
      { key: 'gasOut',     label: 'Lean gas',   kind: 'out', phase: 'V', side: 'top',    pct: 0.3 },
      { key: 'solventOut', label: 'Rich oil',   kind: 'out', phase: 'L', side: 'bottom', pct: 0.7 },
    ],
    defaultParams: { name: '', N_stages: 6 },
    solve(params, inlets) {
      const gasIn = inlets.gasIn, solventIn = inlets.solventIn;
      if (!gasIn?.valid || !solventIn?.valid) return { streams: { gasOut: emptyStream('gasOut'), solventOut: emptyStream('solventOut') }, results: { Status: 'Missing inlet' } };
      const { gasOut, solventOut, Q_abs_kW, F_abs_total, trayT, converged } = kremserAbsorber(gasIn, solventIn, params.N_stages, 'gasOut', 'solventOut');
      const topT = trayT ? trayT[0].toFixed(1) : '—';
      const botT = trayT ? trayT[trayT.length - 1].toFixed(1) : '—';
      return {
        streams: { gasOut, solventOut },
        results: {
          'P (bar)': gasIn.P_bar.toFixed(1),
          'Abs rate': (F_abs_total / gasIn.F_kmols * 100).toFixed(1) + '%',
          'Gas in (kmol/h)': gasIn.F_kmols.toFixed(1),
          'Lean oil in (kmol/h)': solventIn.F_kmols.toFixed(1),
          'Lean gas out (kmol/h)': gasOut.F_kmols.toFixed(1),
          'Rich oil out (kmol/h)': solventOut.F_kmols.toFixed(1),
          'Balance check': Math.abs((gasOut.F_kmols + solventOut.F_kmols) - (gasIn.F_kmols + solventIn.F_kmols)) < 0.1 ? '✓ OK' : '⚠ drift',
          'Q abs (kW)': Q_abs_kW.toFixed(0),
          'Rich oil T (°C)': (solventOut.T_K - 273.15).toFixed(1),
          'Lean gas T (°C)': (gasOut.T_K - 273.15).toFixed(1),
          'Tray T top→bot': `${topT} → ${botT} °C`,
          'Stages': params.N_stages,
          'Recycle': converged ? '✓ converged' : '⚠ not converged',
        }
      };
    },
  },

  STRIPPER: {
    label: 'Stripper', category: 'separation', color: 'coral',
    w: 70, h: 160,
    ports: [
      { key: 'in',     label: 'Feed in',  kind: 'in',  phase: 'any', side: 'left',   pct: 0.3 },
      { key: 'vapOut', label: 'Vap out',  kind: 'out', phase: 'V',   side: 'top',    pct: 0.5 },
      { key: 'liqOut', label: 'Lean oil', kind: 'out', phase: 'L',   side: 'bottom', pct: 0.5 },
    ],
    defaultParams: {
      name: '', N_stages: 12, refluxRatio: 1.5,
      P_bar: 9.5, rebT_C: 259, condT_C: 90,
      lkIdx: 4, hkIdx: 6,
      sumpVol_m3: 20,
      // _trayState: persistent tray holdup (set automatically by solver)
    },
    solve(params, inlets) {
      const s = inlets.in;
      if (!s?.valid) return { streams: { vapOut: emptyStream('vapOut'), liqOut: emptyStream('liqOut') }, results: { Status: 'No feed' } };
      const { vapOut, liqOut, reboilerDuty_kW, trayT, converged, _state: stripperState } = stripper(
        s, params.N_stages, params.refluxRatio,
        params.P_bar || s.P_bar,
        params.rebT_C + 273.15,
        params.lkIdx ?? 4, params.hkIdx ?? 6,
        params.condT_C != null ? params.condT_C + 273.15 : null,
        'vapOut', 'liqOut',
        params._trayState || null
      );
      // Store returned state — must reassign since trayHoldupColumn creates new {} when state was null
      if (stripperState) params._trayState = stripperState;
      // Wang-Henke: j=0=bottom(reboiler), j=N-1=top — correct the display order
      const nST = trayT ? trayT.length : 0;
      const topT = nST > 0 ? trayT[nST-1].toFixed(1) : '—';
      const botT = nST > 0 ? trayT[0].toFixed(1)     : '—';

      // ── Sump: exponential smoothing — converges exactly at steady state
      const rawLiqFlow = liqOut.F_kmols;
      const tau_s = Math.max(1, params.sumpVol_m3 || 20);
      const alpha_s = 1.0 / (1.0 + tau_s);

      if (!isFinite(params._sump_holdup) || params._sump_holdup < 0) {
        params._sump_holdup = rawLiqFlow;
      }
      const outflow_s = alpha_s * rawLiqFlow + (1 - alpha_s) * params._sump_holdup;
      params._sump_holdup = outflow_s;
      const level_s = Math.min(1, Math.abs(rawLiqFlow - outflow_s) / Math.max(rawLiqFlow * 0.1, 1) * 0.5 + 0.5);

      const smoothedLiqOut = { ...liqOut, F_kmols: outflow_s, z: new Float64Array(liqOut.z) };

      return {
        streams: { vapOut, liqOut: smoothedLiqOut },
        results: {
          'Reb duty (kW)': reboilerDuty_kW.toFixed(0),
          'Gas (kmol/h)': vapOut.F_kmols.toFixed(1),
          'Lean oil (kmol/h)': outflow_s.toFixed(1),
          'Tray T top→bot': `${topT} → ${botT} °C`,
          'Flow smooth (kmol/h)': outflow_s.toFixed(1),
          'Status': converged ? '✓ converged' : '⚠ not converged',
        }
      };
    },
  },

  // ── JT Valve (isenthalpic pressure let-down) ──────────────────────────────
  VALVE: {
    label: 'JT Valve', category: 'utility', color: 'gray',
    w: 50, h: 40,
    ports: [
      { key: 'in',  label: '', kind: 'in',  phase: 'any', side: 'left',  pct: 0.5 },
      { key: 'out', label: '', kind: 'out', phase: 'any', side: 'right', pct: 0.5 },
    ],
    defaultParams: { name: '', P2_bar: 20 },
    solve(params, inlets) {
      const s = inlets.in;
      if (!s?.valid) return { streams: { out: emptyStream('out') }, results: { Status: 'No feed' } };
      const { stream, dT_K, T_out_C, vapourFraction } = jtValve(s, params.P2_bar, 'out');
      return {
        streams: { out: stream },
        results: {
          'In T (°C)': (s.T_K - 273.15).toFixed(1),
          'Out T (°C)': T_out_C.toFixed(1),
          'JT cooling (K)': Math.abs(dT_K).toFixed(1),
          'Out P (bar)': params.P2_bar,
          'Vapour frac': vapourFraction != null ? (vapourFraction * 100).toFixed(1) + '%' : '—',
        },
      };
    },
  },

  // ── Distillation Column (FUG shortcut + rigorous toggle) ──────────────────
  // Covers de-ethanizer, de-propanizer, still column, etc.
  // lkIdx/hkIdx indices: 0=CH4 1=C2H6 2=C3H8 3=C4 4=C5 5=C6 6=N2 7=CO2 8=H2S 9=H2O
  DISTCOL: {
    label: 'Dist Column', category: 'separation', color: 'purple',
    w: 70, h: 180,
    ports: [
      { key: 'feed',     label: 'Feed',    kind: 'in',  phase: 'any', side: 'left',   pct: 0.5  },
      { key: 'overhead', label: 'O/H vap', kind: 'out', phase: 'V',   side: 'top',    pct: 0.35 },
      { key: 'bottoms',  label: 'Btms',    kind: 'out', phase: 'L',   side: 'bottom', pct: 0.5  },
    ],
    defaultParams: {
      name: '',
      rigorous: 0,
      lkIdx: 1, hkIdx: 2,
      rec_lk: 0.95, rec_hk: 0.05,
      R_mult: 1.3,
      N_stages: 12, feedTray: 6,
      R: 2.0, distRate: 0,
      P_bar: 25,
      condT_C: -15,
      rebT_C: 0,
      sumpVol_m3: 10,
      accVol_m3: 5,  // reflux accumulator volume — larger = more composition damping
      weirHold_kmol: 5, // liquid holdup per tray
    },
    solve(params, inlets) {
      const s = inlets.feed;
      if (!s?.valid) return {
        streams: { overhead: emptyStream('overhead'), bottoms: emptyStream('bottoms') },
        results: { Status: 'No feed' },
      };

      try {
        let rawBottomsFlow, rawBottomsZ, rawOverheadFlow, rawOverheadZ, resData;
        let streamOverheadT_K = null, streamBottomsT_K = null;
        const topT_ref = { val: null }, botT_ref = { val: null };

        if (params.rigorous) {
          // ── Tray holdup column (physically realistic) ────────────────────
          // _trayState persists between solve() calls — do NOT reset it here.
          // It is initialised inside trayHoldupColumn on first call when null/undefined.
          const res = trayHoldupColumn(s, params.N_stages, {
            hasCondenser: true,
            condT_K: (params.condT_C ?? -15) + 273.15,
            rebT_K:  (params.rebT_C > 0 ? params.rebT_C : 100) + 273.15,
            refluxRatio: params.R || 2.0,
            feedTray: Math.max(0, Math.min(params.N_stages-1, (params.feedTray || Math.floor(params.N_stages/2)) - 1)),
            P_bar: params.P_bar,
            lkIdx: params.lkIdx ?? 1,
            hkIdx: params.hkIdx ?? 2,
            weirHoldup_kmol: params.weirHold_kmol || 5,
            state: params._trayState || null, // pass existing state (null on first call)
          });
          // Store state back — trayHoldupColumn mutates opts.state and returns it as _state
          params._trayState = res._state;

          rawOverheadFlow = res.overheadFlow;
          rawOverheadZ    = res.overheadZ;
          rawBottomsFlow  = res.bottomsFlow;
          rawBottomsZ     = res.bottomsZ;
          // Stream temperatures come directly from solver (Wang-Henke j=0=bottom)
          streamOverheadT_K = res.T_overhead; // = condT_K
          streamBottomsT_K  = res.T_bottoms;  // = rebT_K

          // ── Reflux accumulator — smooths overhead composition over time ───
          // Physical basis: the overhead drum holds liquid condensate. Its composition
          // changes slowly as new overhead arrives — it cannot flip composition in 1 step.
          // Holdup ~ accVol_m3 * liquid_density_kmolm3
          const LIQDEN = 6.0; // kmol/m3 (C2-C4 liquid average)
          const accHold = (params.accVol_m3 || 5) * LIQDEN; // kmol in accumulator
          if (!params._accZ || params._accZ.length !== N_COMP) {
            // Initialise accumulator with overhead composition
            params._accZ = new Array(N_COMP).fill(0);
            for (let i = 0; i < N_COMP; i++) params._accZ[i] = rawOverheadZ[i];
          }
          // Blend new overhead into accumulator (first-order lag)
          // z_acc_new = (accHold*z_acc + F_ovhd*z_new) / (accHold + F_ovhd)
          const denom = accHold + rawOverheadFlow;
          const z_acc_new = new Array(N_COMP).fill(0);
          for (let i = 0; i < N_COMP; i++) {
            z_acc_new[i] = (accHold * params._accZ[i] + rawOverheadFlow * rawOverheadZ[i]) / denom;
          }
          params._accZ = z_acc_new;

          // ── Physical clamp: cannot have K<<1 components in overhead ───────
          // At condenser temperature, components with K < 0.01 CANNOT be vapour.
          // If the tray solver puts them there, zero them out and renormalise.
          const condT_K = (params.condT_C ?? -15) + 273.15;
          const K_cond = COMP_LIST.map(c => {
            const Tr = condT_K / c.Tc;
            if (Tr >= 1) return (c.Pc / params.P_bar) * Math.exp(5.373 * (1 + c.omega) * (1 - c.Tc / condT_K));
            const f0 = 5.92714 - 6.09648/Tr - 1.28862*Math.log(Tr) + 0.169347*Math.pow(Tr,6);
            const f1 = 15.2518 - 15.6875/Tr - 13.4721*Math.log(Tr) + 0.43577*Math.pow(Tr,6);
            return Math.exp(Math.max(-50, Math.min(50, f0 + f1*c.omega))) * c.Pc / params.P_bar;
          });

          // Enforce: overhead composition cannot have heavy components (K < 0.01)
          // These are physically impossible in the vapour at condenser conditions
          const z_clamped = new Float64Array(N_COMP);
          let clamped_sum = 0;
          for (let i = 0; i < N_COMP; i++) {
            // Use accumulator composition but zero out heavy components
            z_clamped[i] = K_cond[i] > 0.01 ? params._accZ[i] : 0;
            clamped_sum += z_clamped[i];
          }
          if (clamped_sum > 1e-9) {
            for (let i = 0; i < N_COMP; i++) z_clamped[i] /= clamped_sum;
          } else {
            // Fallback: use K-weighted composition
            let ksum = 0;
            for (let i = 0; i < N_COMP; i++) { z_clamped[i] = K_cond[i] * s.z[i]; ksum += z_clamped[i]; }
            if (ksum > 0) for (let i = 0; i < N_COMP; i++) z_clamped[i] /= ksum;
          }
          rawOverheadZ = z_clamped;

          // Wang-Henke trayT: j=0=bottom (reboiler), j=N-1=top (condenser)
          const nT = res.trayT ? res.trayT.length : 0;
          topT_ref.val = nT > 0 ? res.trayT[nT-1] : (params.condT_C ?? -15); // top = last
          botT_ref.val = nT > 0 ? res.trayT[0]    : (params.rebT_C || 100);  // bot = first
          resData = {
            Mode: 'Rigorous (' + (res.converged ? '✓' : '!') + ' ' + res.iterations + ' iter)',
            'Stages': params.N_stages,
            'R (L/D)': (params.R || 2).toFixed(2),
            'Cond duty (kW)': res.condDuty_kW.toFixed(0),
            'Reb duty (kW)':  res.rebDuty_kW.toFixed(0),
            'Cond T (°C)': topT_ref.val.toFixed(1),   // topT_ref = condenser (j=N-1)
            'Reb T (°C)':  botT_ref.val.toFixed(1),   // botT_ref = reboiler (j=0)
            'Tray T profile': `Top: ${topT_ref.val.toFixed(1)}°C → Bottom: ${botT_ref.val.toFixed(1)}°C`,
          };
          botT_ref.val = res.trayT[res.trayT.length - 1];
          resData = {
            Mode: 'Rigorous (' + (res.converged ? '✓' : '!') + ' ' + res.iterations + ' iter)',
            'Stages': params.N_stages,
            'R (L/D)': res.R.toFixed(2),
            'Cond duty (kW)': res.condDuty_kW.toFixed(0),
            'Reb duty (kW)':  res.rebDuty_kW.toFixed(0),
            'Cond T (°C)': topT_ref.val.toFixed(1),
            'Reb T (°C)': botT_ref.val.toFixed(1),
            'Tray T profile': `Top: ${topT_ref.val.toFixed(1)}°C → Bottom: ${botT_ref.val.toFixed(1)}°C`,
          };
        } else {
          const res = fugDistillation(s, params, 'overhead', 'bottoms');
          rawBottomsFlow  = res.bottomsStream.F_kmols;
          rawBottomsZ     = res.bottomsStream.z;
          rawOverheadFlow = res.overheadStream.F_kmols;
          rawOverheadZ    = res.overheadStream.z;
          streamOverheadT_K = res.overheadStream.T_K;
          streamBottomsT_K  = res.bottomsStream.T_K;
          const lkName = COMP_LIST[params.lkIdx]?.name?.replace('n-', '') || '?';
          const hkName = COMP_LIST[params.hkIdx]?.name?.replace('n-', '') || '?';
          resData = {
            Mode: 'Shortcut (FUG)',
            [`${lkName}/${hkName}`]: `${(params.rec_lk*100).toFixed(0)}%/${(params.rec_hk*100).toFixed(0)}%`,
            'Cond duty (kW)': res.condDuty_kW.toFixed(0),
            'Reb duty (kW)':  res.rebDuty_kW.toFixed(0),
            'R / R_min': `${res.R.toFixed(2)} / ${res.R_min.toFixed(2)}`,
            'N_min / N': `${res.N_min.toFixed(1)} / ${res.N_actual}`,
          };
        }

        // ── Sump: exponential smoothing — converges exactly at steady state ──
        // outflow = alpha*raw + (1-alpha)*prev  →  at ss: outflow = rawBottomsFlow exactly.
        // tau = sumpVol_m3 = smoothing time constant in outer iterations.
        const tau = Math.max(1, params.sumpVol_m3 || 10);
        const alpha_sump = 1.0 / (1.0 + tau);
        if (!isFinite(params._sump_holdup) || params._sump_holdup < 0) {
          params._sump_holdup = rawBottomsFlow; // initialise to first raw value
        }
        const smoothedBottomsFlow = alpha_sump * rawBottomsFlow + (1 - alpha_sump) * params._sump_holdup;
        params._sump_holdup = smoothedBottomsFlow;

        return {
          streams: {
            overhead: {
              id: 'overhead',
              T_K: streamOverheadT_K || ((params.condT_C ?? -15) + 273.15),
              P_bar: params.P_bar, F_kmols: rawOverheadFlow,
              z: rawOverheadZ, phase: 'V', valid: true,
            },
            bottoms: {
              id: 'bottoms',
              T_K: streamBottomsT_K || ((params.rebT_C > 0 ? params.rebT_C : 100) + 273.15),
              P_bar: params.P_bar, F_kmols: smoothedBottomsFlow,
              z: rawBottomsZ, phase: 'L', valid: true,
            },
          },
          results: {
            ...resData,
            'O/H (kmol/h)': rawOverheadFlow.toFixed(1),
            'Btms (kmol/h)': smoothedBottomsFlow.toFixed(1),
            'Sump τ (iter)': tau.toFixed(0),
          },
        };
      } catch (e) {
        return {
          streams: { overhead: emptyStream('overhead'), bottoms: emptyStream('bottoms') },
          results: { Error: e.message.slice(0, 60) },
        };
      }
    },
  },

  // ── Partial / Total Condenser + Reflux Accumulator ───────────────────────
  // partial=0 → total condenser: all vapour condensed, liquid product + reflux
  //   overhead port carries net liquid (NGL product, C3+)
  //   vapour port is empty (or vent)
  // partial=1 → partial condenser: vapour + liquid split at condT_C
  //   vapour port: non-condensed gas (recycle or sales gas)
  //   liquid port: NGL / condensate product
  PARTIAL_COND: {
    label: 'Condenser', category: 'separation', color: 'blue',
    w: 90, h: 90,
    ports: [
      { key: 'vapIn',  label: 'O/H in',   kind: 'in',  phase: 'V', side: 'top',    pct: 0.5  },
      { key: 'vapOut', label: 'Vap prod',  kind: 'out', phase: 'V', side: 'right',  pct: 0.28 },
      { key: 'liqOut', label: 'Liq prod',  kind: 'out', phase: 'L', side: 'right',  pct: 0.72 },
    ],
    defaultParams: {
      name: '',
      partial: 1,        // 1 = partial (vap + liq), 0 = total (all liquid)
      condT_C: -15,
      P_bar: 24,
      refluxRatio: 2.0,  // L/D returned to column (internal — reduces net liquid)
    },
    solve(params, inlets) {
      const s = inlets.vapIn;
      if (!s?.valid) return {
        streams: { vapOut: emptyStream('vapOut'), liqOut: emptyStream('liqOut') },
        results: { Status: 'No feed' },
      };

      if (params.partial) {
        // ── Partial condenser ────────────────────────────────────────────
        const { vapOut, liqOut, condDuty_kW, refluxFlow_kmolh } =
          partialCondenser(s, params.condT_C, params.P_bar, params.refluxRatio, 'vapOut', 'liqOut');
        return {
          streams: { vapOut, liqOut },
          results: {
            Mode: 'Partial',
            'Cond duty (kW)': condDuty_kW.toFixed(0),
            'Vap product (kmol/h)': vapOut.F_kmols.toFixed(1),
            'Liq product (kmol/h)': liqOut.F_kmols.toFixed(1),
            'Reflux (kmol/h)': refluxFlow_kmolh.toFixed(1),
            'Cond T (°C)': params.condT_C,
          },
        };
      } else {
        // ── Total condenser (all vapour condensed → liquid NGL product) ──
        const T_cond = params.condT_C + 273.15;
        const P = params.P_bar || s.P_bar;
        const F = s.F_kmols;

        // All condensed — use feed composition as liquid
        const F_liq_total = F;
        const F_liq_net = F_liq_total / (1 + params.refluxRatio);

        let dHvap_avg = 0;
        for (let i = 0; i < N_COMP; i++) {
          if (s.z[i] <= 0) continue;
          const c = COMP_LIST[i];
          const Tr = T_cond / c.Tc;
          if (Tr >= 1) continue;
          dHvap_avg += s.z[i] * R.J_MOL_K * c.Tc *
            Math.max(0, 3.978 * Tr**(1/3) - 3.938 + 1.555 * Math.log(Tr));
        }
        const condDuty_kW = F * dHvap_avg / 3600;
        const refluxFlow = F_liq_total - F_liq_net;

        const liqOut = {
          id: 'liqOut', T_K: T_cond, P_bar: P,
          F_kmols: F_liq_net, z: new Float64Array(s.z), phase: 'L', valid: true,
        };
        const vapOut = emptyStream('vapOut'); // no vapour product in total cond

        return {
          streams: { vapOut, liqOut },
          results: {
            Mode: 'Total (NGL liquid)',
            'Cond duty (kW)': condDuty_kW.toFixed(0),
            'NGL product (kmol/h)': F_liq_net.toFixed(1),
            'Reflux (kmol/h)': refluxFlow.toFixed(1),
            'Cond T (°C)': params.condT_C,
          },
        };
      }
    },
  },
};

// ─── Port position calculator ─────────────────────────────────────────────────
export function portPosition(node, portKey) {
  const def = NODE_TYPES[node.type];
  if (!def) return { x: node.x, y: node.y };
  const port = def.ports.find(p => p.key === portKey);
  if (!port) return { x: node.x, y: node.y };
  const { w, h } = def;
  let px, py;
  switch (port.side) {
    case 'left':   px = node.x;         py = node.y + h * port.pct; break;
    case 'right':  px = node.x + w;     py = node.y + h * port.pct; break;
    case 'top':    px = node.x + w * port.pct; py = node.y;         break;
    case 'bottom': px = node.x + w * port.pct; py = node.y + h;     break;
    default:       px = node.x + w / 2; py = node.y + h / 2;
  }
  return { x: px, y: py };
}

// ─── Palette ──────────────────────────────────────────────────────────────────
export const PALETTE = [
  { category: 'Boundary',   types: ['FEED', 'SINK'] },
  { category: 'Separation', types: ['SEPARATOR', 'ABSORBER', 'STRIPPER', 'DISTCOL', 'PARTIAL_COND'] },
  { category: 'Heat',       types: ['HEATER', 'AIRCOOLER', 'HX', 'CHILLER', 'REBOILER'] },
  { category: 'Rotating',   types: ['COMPRESSOR', 'PUMP'] },
  { category: 'Utility',    types: ['MIXER', 'SPLITTER', 'VALVE'] },
];

// ─── Color palette ────────────────────────────────────────────────────────────
export const COLOR_MAP = {
  teal:   { bg: '#e1f5ee', border: '#0f6e56', text: '#085041' },
  gray:   { bg: '#f1efe8', border: '#5f5e5a', text: '#2c2c2a' },
  blue:   { bg: '#e6f1fb', border: '#185fa5', text: '#0c447c' },
  purple: { bg: '#eeedfe', border: '#534ab7', text: '#3c3489' },
  amber:  { bg: '#faeeda', border: '#854f0b', text: '#633806' },
  coral:  { bg: '#faece7', border: '#993c1d', text: '#711b13' },
  green:  { bg: '#eaf3de', border: '#3b6d11', text: '#27500a' },
};
